import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { schedulesAPI } from '../services/api';
import { Schedule } from '../types';
import { Calendar, Clock, Plus, Edit, Trash2, Save, X, AlertCircle } from 'lucide-react';

const ProviderSchedulePage: React.FC = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [newSchedule, setNewSchedule] = useState({
    date: '',
    timeSlots: [
      { startTime: '09:00', endTime: '11:00', isAvailable: true },
      { startTime: '11:00', endTime: '13:00', isAvailable: true },
      { startTime: '14:00', endTime: '16:00', isAvailable: true },
      { startTime: '16:00', endTime: '18:00', isAvailable: true }
    ]
  });

  // Check if user is a driver
  const isDriver = user?.skills?.includes('driver');

  useEffect(() => {
    if (user?.role === 'provider' && !isDriver) {
      fetchSchedules();
    }
  }, [user, isDriver]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await schedulesAPI.getMySchedules();
      setSchedules(response.data.schedules || response.data || []);
    } catch (error: any) {
      console.error('Error fetching schedules:', error);
      setError(error.response?.data?.message || 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      setError('');
      setSuccess('');
      
      const response = await schedulesAPI.createOrUpdateSchedule({
        ...newSchedule,
        isAvailable: true
      });
      setSchedules([...(schedules || []), response.data.schedule]);
      setSuccess('Schedule created successfully!');
      setShowCreateForm(false);
      setNewSchedule({
        date: '',
        timeSlots: [
          { startTime: '09:00', endTime: '11:00', isAvailable: true },
          { startTime: '11:00', endTime: '13:00', isAvailable: true },
          { startTime: '14:00', endTime: '16:00', isAvailable: true },
          { startTime: '16:00', endTime: '18:00', isAvailable: true }
        ]
      });
    } catch (error: any) {
      console.error('Error creating schedule:', error);
      setError(error.response?.data?.message || 'Failed to create schedule');
    }
  };

  const handleUpdateSchedule = async (schedule: Schedule) => {
    try {
      setError('');
      setSuccess('');
      
      const response = await schedulesAPI.createOrUpdateSchedule({
        date: typeof schedule.date === 'string' ? schedule.date : schedule.date.toISOString().split('T')[0],
        timeSlots: schedule.timeSlots,
        isAvailable: schedule.isAvailable || true
      });
      
      setSchedules(schedules?.map(s => 
        s._id === schedule._id ? response.data.schedule : s
      ) || []);
      setSuccess('Schedule updated successfully!');
      setEditingSchedule(null);
    } catch (error: any) {
      console.error('Error updating schedule:', error);
      setError(error.response?.data?.message || 'Failed to update schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      await schedulesAPI.deleteSchedule(scheduleId);
      setSchedules(schedules?.filter(s => s._id !== scheduleId) || []);
      setSuccess('Schedule deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting schedule:', error);
      setError(error.response?.data?.message || 'Failed to delete schedule');
    }
  };

  const addTimeSlot = (isEdit: boolean = false) => {
    const newSlot = { startTime: '09:00', endTime: '10:00', isAvailable: true };
    
    if (isEdit && editingSchedule) {
      setEditingSchedule({
        ...editingSchedule,
        timeSlots: [...editingSchedule.timeSlots, newSlot]
      });
    } else {
      setNewSchedule({
        ...newSchedule,
        timeSlots: [...newSchedule.timeSlots, newSlot]
      });
    }
  };

  const removeTimeSlot = (index: number, isEdit: boolean = false) => {
    if (isEdit && editingSchedule) {
      setEditingSchedule({
        ...editingSchedule,
        timeSlots: editingSchedule.timeSlots?.filter((_, i) => i !== index) || []
      });
    } else {
      setNewSchedule({
        ...newSchedule,
        timeSlots: newSchedule.timeSlots?.filter((_, i) => i !== index) || []
      });
    }
  };

  const updateTimeSlot = (index: number, field: string, value: string, isEdit: boolean = false) => {
    if (isEdit && editingSchedule) {
      const updatedSlots = [...editingSchedule.timeSlots];
      updatedSlots[index] = { ...updatedSlots[index], [field]: value };
      setEditingSchedule({
        ...editingSchedule,
        timeSlots: updatedSlots
      });
    } else {
      const updatedSlots = [...newSchedule.timeSlots];
      updatedSlots[index] = { ...updatedSlots[index], [field]: value };
      setNewSchedule({
        ...newSchedule,
        timeSlots: updatedSlots
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Drivers don't need schedules - they accept requests on-demand
  if (isDriver) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-black500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Schedules Not Needed</h2>
          <p className="text-gray-300 mb-2">As a driver, you don't need to manage schedules.</p>
          <p className="text-gray-300">You'll receive driver requests on-demand that you can accept or decline.</p>
        </div>
      </div>
    );
  }

  if (user?.role !== 'provider') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300">This page is only accessible to service providers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with User Photo */}
      <div className="bg-gradient-to-r from-black to-gray-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center space-x-6">
            {user?.avatar ? (
              <img 
                src={user.avatar} 
                alt={user.name || 'User'} 
                className="h-20 w-20 rounded-full border-4 border-white shadow-lg object-cover"
              />
            ) : (
              <div className="h-20 w-20 rounded-full border-4 border-white shadow-lg bg-white bg-opacity-20 flex items-center justify-center">
                <Calendar className="h-10 w-10 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-white">Availability Management</h1>
              <p className="text-white mt-1">Manage your service availability and time slots</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Schedule Button */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Schedule
          </button>
        </div>
        {/* Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600">{success}</p>
          </div>
        )}

        {/* Create Schedule Form */}
        {showCreateForm && (
          <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Schedule</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={newSchedule.date}
                  onChange={(e) => setNewSchedule({ ...newSchedule, date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Time Slots</label>
                  <button
                    type="button"
                    onClick={() => addTimeSlot(false)}
                    className="text-black hover:text-gray-800 text-sm"
                  >
                    + Add Slot
                  </button>
                </div>
                
                <div className="space-y-2">
                  {newSchedule.timeSlots?.map((slot, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={slot.startTime}
                        onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      />
                      <span>to</span>
                      <input
                        type="time"
                        value={slot.endTime}
                        onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeTimeSlot(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleCreateSchedule}
                  disabled={!newSchedule.date || newSchedule.timeSlots?.length === 0}
                  className="flex items-center bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 disabled:bg-gray-400 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Create Schedule
                </button>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedules List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading schedules...</p>
          </div>
        ) : schedules?.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No schedules yet</h3>
            <p className="text-gray-600 mb-6">Create your first schedule to start accepting bookings.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {schedules?.map((schedule) => (
              schedule && (
                <div key={schedule._id} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {formatDate(typeof schedule.date === 'string' ? schedule.date : schedule.date.toISOString())}
                      </h3>
                    <p className="text-sm text-gray-600">
                      {schedule.timeSlots?.length || 0} time slots
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingSchedule(schedule)}
                      className="flex items-center text-black hover:text-gray-800"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule._id)}
                      className="flex items-center text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>

                {editingSchedule?._id === schedule._id ? (
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700">Time Slots</label>
                        <button
                          type="button"
                          onClick={() => addTimeSlot(true)}
                          className="text-black hover:text-gray-800 text-sm"
                        >
                          + Add Slot
                        </button>
                      </div>
                      
                      <div className="space-y-2">
                        {editingSchedule.timeSlots?.map((slot, index) => (
                          <div key={index} className="flex items-center space-x-2">
                            <input
                              type="time"
                              value={slot.startTime}
                              onChange={(e) => updateTimeSlot(index, 'startTime', e.target.value, true)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              disabled={!!slot.bookingId}
                            />
                            <span>to</span>
                            <input
                              type="time"
                              value={slot.endTime}
                              onChange={(e) => updateTimeSlot(index, 'endTime', e.target.value, true)}
                              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              disabled={!!slot.bookingId}
                            />
                            {slot.bookingId ? (
                              <span className="text-sm text-red-600">Booked</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => removeTimeSlot(index, true)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleUpdateSchedule(editingSchedule)}
                        className="flex items-center bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </button>
                      <button
                        onClick={() => setEditingSchedule(null)}
                        className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {schedule.timeSlots?.map((slot, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg border ${
                          slot.bookingId
                            ? 'bg-red-50 border-red-200 text-red-800'
                            : (slot.isAvailable !== false && !slot.bookingId)
                            ? 'bg-green-50 border-green-200 text-green-800'
                            : 'bg-gray-50 border-gray-200 text-gray-800'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-2" />
                            <span className="font-medium">
                              {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                            </span>
                          </div>
                          <span className="text-xs">
                            {slot.bookingId ? 'Booked' : (slot.isAvailable !== false && !slot.bookingId) ? 'Available' : 'Unavailable'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderSchedulePage; 